import type { Basic, Reference } from '@medplum/fhirtypes'
import { Label } from '@seen/core/models/label'
import { IconPlus } from '@tabler/icons-react'
import { type ReactNode, useMemo, useState } from 'react'
import { ComboboxCommand } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxMultiCommand } from '#src/components/core/combobox/ComboboxMulti.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { LabelOption } from '#src/components/LabelOption.tsx'

export function LabelCombobox<TValue extends string>({
  labels,
  categories,
  value,
  onChange,
  children,
  onCreate,
  isCreating,
}: {
  labels:
    | {
        reference: Reference<Basic> & { reference: string }
        color: string
        name: string
      }[]
    | undefined
  /**
   * Hard-coded, pick-only options rendered as identical colored chips alongside
   * the user labels. Their `value` is the persisted token (not a `Basic`
   * reference); the consumer splits them back out on change. No "create new"
   * affordance applies to these.
   */
  categories?: { value: string; color: string; name: string }[]
  value: TValue[]
  onChange: (value: TValue[]) => any
  onCreate: (name: string, color: string) => void
  children: ReactNode
  isCreating: boolean
}): ReactNode {
  const options = useMemo(
    () => [
      ...(labels ?? []).map((l) => ({
        value: l.reference.reference,
        label: <LabelOption color={l.color} label={l.name} />,
        keywords: [l.name],
      })),
      ...(categories ?? []).map((c) => ({
        value: c.value,
        label: <LabelOption color={c.color} label={c.name} />,
        keywords: [c.name],
      })),
    ],
    [labels, categories]
  )
  const [pickingLabel, setPickingLabel] = useState(false)
  const [pickingColor, setPickingColor] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  return (
    <Popover
      open={pickingLabel || pickingColor || isCreating}
      onOpenChange={(o) => {
        setPickingLabel(o)
        setPickingColor(false)
        setNewLabelName('')
      }}
    >
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        {pickingLabel && (
          <ComboboxMultiCommand
            options={options}
            selectedValues={value}
            onSelect={(value, close) => {
              onChange(value as TValue[])
              if (close) {
                setPickingLabel(false)
              }
            }}
            createLabel={(search) => (
              <div className="flex items-center gap-2 px-0.5">
                <IconPlus className="size-4" />
                <span>
                  Create new label:{' '}
                  <span className="text-foreground-secondary">{search}</span>
                </span>
              </div>
            )}
            onCreate={(search) => {
              setPickingLabel(false)
              setPickingColor(true)
              setNewLabelName(search)
            }}
            searchPlaceholder="Add labels…"
          />
        )}
        {pickingColor && (
          <ComboboxCommand
            options={Label.defaultColors.map((c) => ({
              label: <LabelOption color={c.color} label={c.name} />,
              value: c.color,
              keywords: [c.name],
            }))}
            autoFocus
            onSelect={(color) => {
              onCreate(newLabelName, color)
              setPickingColor(false)
              setNewLabelName('')
            }}
            searchPlaceholder="Pick a color for label"
          />
        )}
        {!pickingLabel && !pickingColor && (
          <div className="h-40">
            <Loader />
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
